import * as dto from '@epicuro-next/data/baskets-dto';
import { BasketRowsGroupType } from 'graphql-api/baskets-api-interfaces';

import {
  BasketMappingSettings,
  getAddressMap,
  getBasketAccountsMap,
  getBasketRows,
  getDeliveryAddressMap,
  getItemsMap,
  getNestedObjects,
  getProductType,
  GetRowsFn,
  GroupingFn,
  Mappings,
} from '../mappings';

import { groupByAddress } from './address.grouping';
import { groupByArticleGroup } from './article-group.grouping';
import { groupByArticle } from './article.grouping';
import { groupByBrand } from './brand.grouping';
import { groupByBusinessUnit } from './business-unit.grouping';
import { groupByCategory } from './category.grouping';
import { groupByDelivery } from './delivery.grouping';
import { groupByDivision } from './division.grouping';
import { groupByOrderNumber } from './order-number.grouping';

const groupings: { [category: string]: GroupingFn[] } = Object.freeze({
  [BasketRowsGroupType.Category]: [
    groupByAddress,
    groupByCategory,
    groupByArticle,
  ],
  [BasketRowsGroupType.Delivery]: [
    groupByAddress,
    groupByDelivery,
    groupByArticle,
  ],
  [BasketRowsGroupType.Division]: [
    groupByAddress,
    groupByDivision,
    groupByArticle,
  ],
  [BasketRowsGroupType.Article]: [groupByAddress, groupByArticle],
  [BasketRowsGroupType.Address]: [groupByAddress],
  [BasketRowsGroupType.Brand]: [groupByBrand, groupByAddress],
  [BasketRowsGroupType.Articlegroup]: [
    groupByAddress,
    groupByArticleGroup,
    groupByArticle,
  ],
  [BasketRowsGroupType.Businessunit]: [
    groupByAddress,
    groupByBusinessUnit,
    groupByArticle,
  ],
});

export function getGroupingFunction(name?: string) {
  switch (name) {
    case 'groupByBrand':
      return groupByBrand;
    case 'groupByAddress':
      return groupByAddress;
    case 'groupByArticle':
      return groupByArticle;
    case 'groupByCategory':
      return groupByCategory;
    case 'groupByDelivery':
      return groupByDelivery;
    case 'groupByDivision':
      return groupByDivision;
    case 'groupByOrderNumber':
      return groupByOrderNumber;
    case 'groupByArticleGroup':
      return groupByArticleGroup;
    case 'groupByBusinessUnit':
      return groupByBusinessUnit;
    default:
      return undefined;
  }
}

export function createGroups(
  basketDto: dto.ExtendedDtoBasket,
  products: dto.ProductsMap,
  groupType: BasketRowsGroupType,
  options?: Partial<BasketMappingSettings>,
) {
  const items = getItemsMap(basketDto.items);
  const deliveries = getDeliveryAddressMap(basketDto.basketAccounts);
  const addresses = getAddressMap(deliveries);
  const basketAccounts = getBasketAccountsMap(basketDto.basketAccounts);

  const settings = {
    basketItemCategoryAttributeKey:
      basketDto.basketItemCategoryAttributeKey || 'categoryname',
    basketItemDivisionAttributeKey:
      options?.basketItemDivisionAttributeKey || 'divisionb2b',
    productTitleLevel: getProductType(options?.productTitleLevelString),
    additionalPrefix: options?.additionalPrefix,
    showIdInArticleGroup: options?.showIdInArticleGroup,
    columnsShowRequestedOnPlaced: options?.columnsShowRequestedOnPlaced,
    showMaterialWidthIds: options?.showMaterialWidthIds,
    manualDiscounts: options?.manualDiscounts,
  };
  const maps: Mappings = {
    items,
    products,
    deliveries,
    addresses,
    settings,
    basketAccounts,
    basket: basketDto,
  };

  const groupingFns = getGroupingFunctions(
    groupType,
    options?.defaultGroupingName,
  );

  return groupingReducer(basketDto.items, maps, groupingFns);
}

export function getGroupingFunctions(
  groupType: BasketRowsGroupType,
  defaultGroupingName?: string,
) {
  let grps = groupings[groupType];
  const defaultGrouping = getGroupingFunction(defaultGroupingName);
  if (defaultGrouping && grps.indexOf(defaultGrouping) < 0) {
    grps = [defaultGrouping, ...grps];
  }

  return [groupByOrderNumber, ...grps];
}

function groupingReducer(
  items: dto.Item[],
  maps: Mappings,
  groupingFns: GroupingFn[],
) {
  const copy = groupingFns?.slice();
  const last = copy?.pop();
  if (!last) {
    throw new Error('At least one grouping function should be defined.');
  }

  return copy.reduceRight(
    (nestedGroupingFn, groupingFn) => (i, m) =>
      groupingFn(i, m, nestedGroupingFn, getBasketRows),
    (i, m) => last(i, m, undefined, getBasketRows),
  )(items, maps);
}

export function addNestedObjectsToMapping(
  attributeItemIdsMap: { [key: string]: string[] },
  maps: Mappings,
  basketRowsGroupType: BasketRowsGroupType,
  groupFn?: GroupingFn,
  getRowsFn?: GetRowsFn,
) {
  return Object.keys(attributeItemIdsMap).map((attribute) => {
    const articleGroupItems = attributeItemIdsMap[attribute].map(
      (itemId) => maps.items[itemId],
    );
    const nested = getNestedObjects(
      articleGroupItems,
      maps,
      groupFn,
      getRowsFn,
    );

    return {
      id: attribute,
      type: basketRowsGroupType,
      title: attribute,
      ...nested,
    };
  });
}
